import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ColumnConfig, FieldType, GenericTableComponent, GlobalAction, HeaderConfigurationService, ModalFieldConfig, ModalMode, TableAction, TableActionConfig } from '@solcre-org/core-ui';
import { environment } from '../../../../../environments/environment.local';
import { VesselFuelModel } from '../../../../shared/models/vessel-fuel.model';
import { FUELS, getFuelByCode } from '../../../../shared/data/fuels';
import { getSpeedByCode, SPEEDS } from '../../../../shared/data/speeds';
import { DateUtils } from '../../../../shared/utils/date.utils';

@Component({
  selector: 'app-vessel-fuel',
  imports: [CommonModule, TranslateModule, GenericTableComponent],
  templateUrl: './vessel-fuel.html'
})
export class VesselFuel {
  private headerConfigurationService = inject(HeaderConfigurationService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private translateService = inject(TranslateService);
  private fuels = FUELS;
  private speeds = SPEEDS;

  vesselId = signal<number | null>(null);
  deck = signal<VesselFuelModel | null>(null);

  endpoint = computed(() => {
    return environment.API.VESSELS_FUEL_URI.replace(':id', this.vesselId()?.toString() ?? '');
  });

  columns = computed<ColumnConfig<VesselFuelModel>[]>(() => [
    {
      key: 'speedName',
      label: this.translateService.instant('vessels.fuel.speed_name'),
      format: (value: string) => {
        const speed = getSpeedByCode(value);
        return speed?.label || value;
      }
    },
    {
      key: 'speedKnots',
      label: this.translateService.instant('vessels.fuel.speed_knots'),
    },
    {
      key: 'consumptionTonsPerDay',
      label: this.translateService.instant('vessels.fuel.consumption_tons_per_day'),
    },
    {
      key: 'fuelType',
      label: this.translateService.instant('vessels.fuel.fuel_type'),
      format: (value: string) => {
        const fuel = getFuelByCode(value);
        return fuel?.label || value;
      }
    },
    {
      key: 'createdAt',
      label: this.translateService.instant('vessels.fuel.created_at'),
      format: (value: any) => DateUtils.format(value)
    },
  ]);

  modalFields = computed<ModalFieldConfig<VesselFuelModel>[]>(() => [
    {
      key: 'speedName',
      label: this.translateService.instant('vessels.fuel.speed_name'),
      placeholder: 'vessels.fuel.speed_name_placeholder',
      type: FieldType.SELECT,
      searchable: true,
      options: this.speeds,
      validators: [Validators.required],
      defaultValue: "",
      readonly: false,
      errorMessages: {
        required: this.translateService.instant('validators.required'),
      }
    },
    {
      key: 'speedKnots',
      label: this.translateService.instant('vessels.fuel.speed_knots'),
      placeholder: 'vessels.fuel.speed_knots_placeholder',
      type: FieldType.NUMBER,
      validators: [Validators.required, Validators.min(0.01)],
      readonly: false,
      errorMessages: {
        required: this.translateService.instant('validators.required'),
        min: this.translateService.instant('validators.min', { min: 0.01 }),
      }
    },
    {
      key: 'consumptionTonsPerDay',
      label: this.translateService.instant('vessels.fuel.consumption_tons_per_day'),
      placeholder: 'vessels.fuel.consumption_tons_per_day_placeholder',
      type: FieldType.NUMBER,
      validators: [Validators.required, Validators.min(0.01)],
      readonly: false,
      errorMessages: {
        required: this.translateService.instant('validators.required'),
        min: this.translateService.instant('validators.min', { min: 0.01 }),
      }
    },
    {
      key: 'fuelType',
      label: this.translateService.instant('vessels.fuel.fuel_type'),
      placeholder: 'vessels.fuel.fuel_type_placeholder',
      type: FieldType.SELECT,
      searchable: true,
      options: this.fuels,
      validators: [Validators.required],
      defaultValue: "",
      readonly: false,
      errorMessages: {
        required: this.translateService.instant('validators.required'),
      }
    },
    {
      key: 'createdAt',
      label: this.translateService.instant('vessels.fuel.created_at'),
      type: FieldType.DATE,
      readonly: true,
      modes: {
        [ModalMode.CREATE]: { visible: false, includeInPayload: false },
        [ModalMode.EDIT]: { visible: false, includeInPayload: false },
        [ModalMode.VIEW]: { visible: true, includeInPayload: false },
      },
    },
  ] as any as ModalFieldConfig<VesselFuelModel>[]);

  actions = computed<TableActionConfig[]>(() => [
    {
      action: TableAction.VIEW,
      isExtra: false,
    },
    {
      action: TableAction.EDIT,
      isExtra: false,
    },
    {
      action: TableAction.DELETE,
      isExtra: true,
    },
    {
      action: TableAction.CREATE,
      isExtra: false,
    }
  ]);

  globalCustomActions = computed<GlobalAction<VesselFuelModel>[]>(() => [
    {
      action: 'goBackToVessels',
      label: this.translateService.instant('vessels.fuel.back_to_vessels'),
      icon: 'icon-arrow-left',
      callback: () => this.goBackToGroups(),
      class: 'c-btn c-btn--stroked',
    }
  ]);

  modelFactory = (data: any) => new VesselFuelModel().fromJSON(data);

  ngOnInit() {
    this.headerConfigurationService.setEndpointHeaders(this.endpoint(), {
      patch: [
        { 'Content-Type': 'application/merge-patch+json' },
      ]
    });
  }

  constructor() {
    this.route.paramMap.subscribe((params: any) => {
      const id = params.get('id');
      if (id) {
        const vesselId = parseInt(id, 10);
        this.vesselId.set(vesselId);
      }
    });
  }

  goBackToGroups(): void {
    this.router.navigate(['/app/vessels']);
  }

  onModalData(data: VesselFuelModel): void {
    if (data) {
      data.id = data.id;
    }
  }

}
